import React, { ReactNode } from 'react';
import '/node_modules/react-grid-layout/css/styles.css';
import '/node_modules/react-resizable/css/styles.css';
import { IconButton } from '@mui/material';
import { styled } from '@mui/system';
import { layoutEditorActions } from '@redux/store/layout-editor/layoutEditorSlice';
import { useSelector } from '@redux/store/store';
import { EyeOff, Pencil } from 'lucide-react';
import { useDispatch } from 'react-redux';
import {
  DeleteIcon,
  layerImageWrapperDivStyle,
  settingPanelStyle,
  useAdjustElementSizeToPreview,
} from '@/components';
import { useLoadRealTimePreviewRasterFontQuery } from '@/hooks';
import { TextElementDTO } from '@/model';

interface LayoutEditorCanvasTextItemProps {
  element: TextElementDTO;
  openDeleteModal: (id: number, name: string) => void;
  currentScale: number;
  displayText: string;
}

const LayoutEditorCanvasTextItem = ({
  element,
  openDeleteModal,
  currentScale,
  displayText,
}: LayoutEditorCanvasTextItemProps) => {
  const dispatch = useDispatch();

  const { editElementModalConfig, selectedLayoutId, fetchedLayout } = useSelector(
    (state) => state.layoutEditor,
  );
  const isQueryEnabled = true;

  const { data: { data: fetchedPreview } = {} } = useLoadRealTimePreviewRasterFontQuery(
    isQueryEnabled,
    {
      text: displayText,
      fontColor: element?.fontColor,
      backgroundColor: element?.backgroundColor,
      rasterFontId: element?.rasterFontId,
      width: Number(element?.width),
      height: Number(element?.height),
      anchorX: element?.anchorX,
      anchorY: element?.anchorY,
      blackAndWhite: false,
    },
  );

  // TODO: investigate if this auto-resize logic can be refactored or handled more elegantly
  useAdjustElementSizeToPreview(
    element,
    fetchedPreview,
    { width: fetchedLayout.displayHeight, height: fetchedLayout.displayHeight },
    (updatedElement) =>
      dispatch(layoutEditorActions.updateElementPosition(updatedElement)),
  );

  const isSelected = element.id === selectedLayoutId;

  const onLayerSelect = () => {
    dispatch(layoutEditorActions.selectLayoutId(element.id));
  };

  const onEditClick = () =>
    dispatch(layoutEditorActions.toggleEditElementModalExpanded());

  const toggleElementVisibility = () => {
    dispatch(
      layoutEditorActions.setElementVisibility({
        id: element.id,
        visible: false,
      }),
    );
  };

  const onDeleteModal = () => {
    openDeleteModal(element.id, element.textValue);
  };

  //TODO: duplicate
  const renderSettingMenu = (): ReactNode => {
    if (!isSelected) return null;

    const inverseScale = (1 / currentScale) * 0.9;
    return (
      <div
        style={{
          ...settingPanelStyle,
          transform: `scale(${inverseScale})`,
        }}
      >
        <StyledSettingsIconButton
          color='primary'
          size='small'
          onClick={() => toggleElementVisibility()}
        >
          <EyeOff />
        </StyledSettingsIconButton>
        {!editElementModalConfig.isExpanded ? (
          <StyledSettingsIconButton color='primary' size='small' onClick={onEditClick}>
            <Pencil />
          </StyledSettingsIconButton>
        ) : null}
        <StyledSettingsIconButton color='primary' size='small' onClick={onDeleteModal}>
          <DeleteIcon />
        </StyledSettingsIconButton>
      </div>
    );
  };

  const renderLayout = () => {
    return (
      <div
        key={element.id}
        onClick={onLayerSelect}
        style={{
          ...layerImageWrapperDivStyle(null, null),
          position: 'relative',
        }}
      >
        <img src={`data:image/png;base64,${fetchedPreview?.content}`} alt='' />
      </div>
    );
  };

  return (
    <>
      {renderSettingMenu()}
      {renderLayout()}
    </>
  );
};

export default LayoutEditorCanvasTextItem;

const StyledSettingsIconButton = styled(IconButton)(() => ({
  width: '30px !important',
  height: '30px !important',
  borderRadius: '50% !important',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgb(231,231,231)',
    transform: 'scale(1.1)',
  },

  '&:active': {
    transform: 'scale(0.95)',
  },
}));
